import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { LuCheck, LuChevronDown, LuLoader, LuUsers, LuX } from 'react-icons/lu';
import axiosInstance from '../../api/axiosInstance';

const MENU_MAX_HEIGHT = 280;
const MENU_GAP = 4;

const normalizeEmail = (email) => String(email || '').trim().toLowerCase();

const getMenuPosition = (triggerEl) => {
  if (!triggerEl) return null;

  const rect = triggerEl.getBoundingClientRect();
  const spaceBelow = window.innerHeight - rect.bottom;
  const spaceAbove = rect.top;
  const openAbove = spaceBelow < MENU_MAX_HEIGHT && spaceAbove > spaceBelow;

  return {
    position: 'fixed',
    left: rect.left,
    width: rect.width,
    maxHeight: MENU_MAX_HEIGHT,
    zIndex: 20000,
    ...(openAbove
      ? { bottom: window.innerHeight - rect.top + MENU_GAP, top: 'auto' }
      : { top: rect.bottom + MENU_GAP, bottom: 'auto' }),
  };
};

/**
 * Multi-select of project members' emails.
 * Members are loaded from /projects/list_project_members for the given project.
 * Menu is portaled to document.body so it is not clipped by modal overflow.
 */
const NotificationEmailSelect = ({
  id,
  projectId,
  value = [],
  onChange,
  disabled = false,
  placeholder = 'Select members to notify',
  'aria-labelledby': ariaLabelledBy,
}) => {
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersError, setMembersError] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState(null);
  const [query, setQuery] = useState('');

  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const searchRef = useRef(null);

  // Fetch project members whenever the project changes
  useEffect(() => {
    if (!projectId || projectId === 'all') {
      setMembers([]);
      return undefined;
    }

    let cancelled = false;
    const fetchMembers = async () => {
      setMembersLoading(true);
      setMembersError(null);
      try {
        const response = await axiosInstance.get(
          `/projects/list_project_members?project_id=${projectId}`,
          { skipGlobalToast: true, skipGlobalLoader: true }
        );
        if (cancelled) return;
        const list = Array.isArray(response?.data) ? response.data : [];
        setMembers(list.filter((member) => normalizeEmail(member?.email)));
      } catch (err) {
        if (cancelled) return;
        console.error('Error fetching project members:', err);
        setMembers([]);
        setMembersError('Failed to load project members.');
      } finally {
        if (!cancelled) setMembersLoading(false);
      }
    };

    fetchMembers();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const selectedSet = useMemo(() => new Set(value.map(normalizeEmail)), [value]);

  const memberEmails = useMemo(
    () => members.map((member) => normalizeEmail(member.email)),
    [members]
  );

  const filteredMembers = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return members;
    return members.filter(
      (member) =>
        String(member?.name || '').toLowerCase().includes(term) ||
        normalizeEmail(member?.email).includes(term)
    );
  }, [members, query]);

  const allSelected =
    memberEmails.length > 0 && memberEmails.every((email) => selectedSet.has(email));

  const updateMenuPosition = useCallback(() => {
    const next = getMenuPosition(triggerRef.current);
    if (next) setMenuPosition(next);
  }, []);

  const closeMenu = useCallback(() => {
    setIsOpen(false);
    setMenuPosition(null);
    setQuery('');
  }, []);

  const openMenu = () => {
    if (disabled) return;
    setMenuPosition(getMenuPosition(triggerRef.current));
    setIsOpen(true);
  };

  useEffect(() => {
    if (!isOpen) return undefined;

    updateMenuPosition();
    window.addEventListener('scroll', updateMenuPosition, true);
    window.addEventListener('resize', updateMenuPosition);

    return () => {
      window.removeEventListener('scroll', updateMenuPosition, true);
      window.removeEventListener('resize', updateMenuPosition);
    };
  }, [isOpen, updateMenuPosition]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event) => {
      const target = event.target;
      if (containerRef.current?.contains(target) || menuRef.current?.contains(target)) {
        return;
      }
      closeMenu();
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeMenu();
        triggerRef.current?.focus();
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, closeMenu]);

  useEffect(() => {
    if (isOpen && menuPosition) searchRef.current?.focus();
  }, [isOpen, menuPosition]);

  const toggleEmail = (email) => {
    const normalized = normalizeEmail(email);
    if (!normalized) return;
    if (selectedSet.has(normalized)) {
      onChange(value.filter((item) => normalizeEmail(item) !== normalized));
    } else {
      onChange([...value, normalized]);
    }
  };

  const toggleSelectAll = () => {
    if (allSelected) {
      onChange(value.filter((item) => !memberEmails.includes(normalizeEmail(item))));
      return;
    }
    onChange(Array.from(new Set([...value.map(normalizeEmail), ...memberEmails])));
  };

  const handleTriggerKeyDown = (event) => {
    if (disabled) return;
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown') {
      event.preventDefault();
      if (!isOpen) openMenu();
    }
  };

  const listboxId = `${id || 'notification-emails'}-listbox`;
  const noProject = !projectId || projectId === 'all';

  return (
    <div ref={containerRef} className="relative">
      <div
        id={id}
        ref={triggerRef}
        role="combobox"
        tabIndex={disabled ? -1 : 0}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={listboxId}
        aria-labelledby={ariaLabelledBy}
        aria-disabled={disabled || undefined}
        onClick={() => (isOpen ? closeMenu() : openMenu())}
        onKeyDown={handleTriggerKeyDown}
        className={`flex min-h-[42px] w-full items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-left focus:outline-none focus:ring-2 focus:ring-blue-500 ${
          disabled ? 'cursor-not-allowed bg-gray-100 text-gray-400' : 'cursor-pointer'
        }`}
      >
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
          {value.length === 0 ? (
            <span className="truncate text-gray-500">{placeholder}</span>
          ) : (
            value.map((email) => (
              <span
                key={email}
                className="inline-flex max-w-full items-center gap-1 rounded-md bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700"
              >
                <span className="truncate">{email}</span>
                {!disabled && (
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      toggleEmail(email);
                    }}
                    className="text-blue-400 transition hover:text-blue-600"
                    aria-label={`Remove ${email}`}
                  >
                    <LuX size={12} />
                  </button>
                )}
              </span>
            ))
          )}
        </div>
        <LuChevronDown
          size={14}
          aria-hidden="true"
          className={`flex-shrink-0 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </div>

      {isOpen &&
        menuPosition &&
        createPortal(
          <div
            ref={menuRef}
            style={menuPosition}
            className="box-border flex flex-col overflow-hidden rounded-lg border border-gray-300 bg-white shadow-lg"
          >
            <div className="flex items-center gap-2 border-b border-gray-100 p-2">
              <input
                ref={searchRef}
                type="text"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search members"
                className="min-w-0 flex-1 rounded-md border border-gray-200 px-2 py-1.5 text-sm text-gray-700 outline-none focus:border-blue-400"
              />
              {memberEmails.length > 0 && (
                <button
                  type="button"
                  onClick={toggleSelectAll}
                  className="shrink-0 text-xs font-medium text-blue-600 transition hover:text-blue-700"
                >
                  {allSelected ? 'Clear all' : 'Select all'}
                </button>
              )}
            </div>

            <div className="flex-1 overflow-y-auto">
              {noProject ? (
                <div className="px-3 py-6 text-center text-xs text-gray-400">
                  Select a project to load members.
                </div>
              ) : membersLoading ? (
                <div className="flex items-center justify-center gap-2 px-3 py-6 text-xs text-gray-400">
                  <LuLoader size={14} className="animate-spin" />
                  Loading members…
                </div>
              ) : membersError ? (
                <div className="px-3 py-6 text-center text-xs text-red-500">{membersError}</div>
              ) : filteredMembers.length === 0 ? (
                <div className="flex flex-col items-center gap-1.5 px-3 py-6 text-center text-xs text-gray-400">
                  <LuUsers size={18} />
                  {members.length === 0 ? 'No members found for this project.' : 'No matching members.'}
                </div>
              ) : (
                <ul id={listboxId} role="listbox" aria-multiselectable="true" className="py-1">
                  {filteredMembers.map((member) => {
                    const email = normalizeEmail(member.email);
                    const checked = selectedSet.has(email);
                    return (
                      <li
                        key={member?.user_id ?? email}
                        role="option"
                        aria-selected={checked}
                        tabIndex={0}
                        onClick={() => toggleEmail(email)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            toggleEmail(email);
                          }
                        }}
                        className={`flex cursor-pointer items-center gap-3 px-3 py-2 outline-none hover:bg-gray-50 focus:bg-gray-50 ${
                          checked ? 'bg-blue-50/60' : ''
                        }`}
                      >
                        <span
                          className="flex h-4 w-4 shrink-0 items-center justify-center rounded border"
                          style={
                            checked
                              ? { borderColor: '#2563eb', backgroundColor: '#eff6ff', color: '#2563eb' }
                              : { borderColor: '#d1d5db', backgroundColor: '#ffffff' }
                          }
                        >
                          {checked && <LuCheck size={12} strokeWidth={3} />}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium text-gray-700">
                            {member?.name || member?.email}
                          </span>
                          <span className="block truncate text-xs text-gray-400">{member?.email}</span>
                        </span>
                        {member?.role && (
                          <span className="shrink-0 rounded-md bg-gray-100 px-2 py-0.5 text-[11px] font-medium capitalize text-gray-500">
                            {member.role}
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default NotificationEmailSelect;
